import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, HelpCircle, Check } from "lucide-react";
import { Seo, serviceSchema, breadcrumbSchema, faqSchema } from "@/components/Seo";
import { SERVICE_HUB, SERVICE_HUB_MAP } from "@/data/servicesHub";
import { SERVICE_ICONS } from "@/pages/ServicesHub";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICE_HUB_MAP[slug];
  if (!s) return <Navigate to="/services" replace />;

  const Icon = SERVICE_ICONS[s.iconKey];
  const others = SERVICE_HUB.filter((x) => x.slug !== slug).slice(0, 4);

  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title={s.metaTitle}
        description={s.metaDescription}
        path={`/services/${s.slug}`}
        jsonLd={[
          serviceSchema({ name: s.name, description: s.metaDescription, path: `/services/${s.slug}`, serviceType: s.eyebrow }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: s.name, path: `/services/${s.slug}` },
          ]),
          faqSchema(s.faqs),
        ]}
      />

      {/* hero */}
      <section className="relative overflow-hidden py-16 sm:py-20">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(55% 55% at 50% 0%, rgba(59,123,255,0.16), transparent 65%)" }} />
        <div className="relative mx-auto max-w-[900px] px-5">
          <Link to="/services" className="inline-flex items-center gap-2 text-xs font-tech tracking-widest text-[color:var(--chrome-3)] hover:text-[color:var(--cyan)] transition-colors mb-6">
            <ArrowLeft size={14} /> ALL SERVICES
          </Link>
          <div className="h-12 w-12 rounded-xl bg-white/[0.04] border border-[color:var(--line)] flex items-center justify-center text-[color:var(--cyan)] mb-5">
            {Icon && <Icon size={22} />}
          </div>
          <p className="eyebrow mb-3">{s.eyebrow}</p>
          <h1 className="h-display text-4xl sm:text-5xl text-chrome leading-[1.03]">
            {s.h1.split("\n").map((l, i) => (
              <span key={i} className={i === 1 ? "block text-holo mt-1" : "block"}>{l}</span>
            ))}
          </h1>
          <div className="mt-6 space-y-4">
            {s.intro.map((p, i) => (
              <p key={i} className="text-[color:var(--chrome-2)] text-base leading-relaxed">{p}</p>
            ))}
          </div>
          <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm mt-8">
            SUBMIT YOUR CARD FOR EVALUATION <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* content sections */}
      <div className="mx-auto max-w-[900px] px-5 space-y-10">
        {s.sections.map((sec, i) => (
          <section key={i} data-testid={`section-${i}`}>
            <h2 className="font-display font-black text-xl sm:text-2xl text-white leading-tight">{sec.h}</h2>
            {sec.p.map((para, j) => (
              <p key={j} className="text-[color:var(--chrome-2)] leading-relaxed mt-3">{para}</p>
            ))}
          </section>
        ))}

        {/* FAQ */}
        <section>
          <h2 className="font-display font-black text-xl sm:text-2xl text-white mb-5">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {s.faqs.map((f, i) => (
              <div key={i} data-testid={`faq-${i}`} className="rounded-2xl border border-[color:var(--line)] p-5">
                <p className="flex items-start gap-2 text-white font-semibold text-sm">
                  <HelpCircle size={16} className="text-[color:var(--cyan)] mt-0.5 shrink-0" /> {f.q}
                </p>
                <p className="text-sm text-[color:var(--chrome-2)] mt-2 leading-relaxed pl-6">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* disclaimer */}
        <p className="text-xs text-[color:var(--chrome-3)] leading-relaxed border-t border-[color:var(--line)] pt-6">
          Results vary by card, material, age and type of damage. Not every card is a candidate for every service.
          CardScience HQ does not guarantee grading results and is not a grading company. Customers receive an honest
          assessment before advanced work begins.
        </p>

        {/* CTA */}
        <div className="rounded-3xl holo-border holo-glow p-8 sm:p-10 text-center">
          <p className="eyebrow mb-3">Not Sure What Your Card Needs?</p>
          <h2 className="h-display text-2xl sm:text-3xl text-chrome">SEND US YOUR CARD FOR EVALUATION.</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-7">
            <Link to="/submit" className="btn-holo w-full sm:w-auto rounded-xl px-8 py-4 text-sm inline-flex items-center justify-center gap-2">
              START MY CARD EVALUATION <ArrowRight size={16} />
            </Link>
            <Link to="/#pricing" className="btn-ghost w-full sm:w-auto rounded-xl px-8 py-4 text-sm inline-flex items-center justify-center">VIEW PRICING</Link>
          </div>
        </div>

        {/* related services */}
        <section>
          <p className="eyebrow mb-4">More Services</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--line)] p-4 hover:border-[color:var(--cyan)]/60 transition-colors group">
                <span className="text-sm text-[color:var(--chrome-1)] group-hover:text-white transition-colors">{o.name}</span>
                <ArrowRight size={15} className="text-[color:var(--cyan)]" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
